import axios from "axios";
import config from "@Config/index";
import setCookie from "helpers/setCookie";
import getCookie from "helpers/getCookie";

let userCountryPromise: Promise<any> | null = null;

/**
 * Region types that used to follow the visitor (P1D-5550). Search pages now show destination
 * country reviews, and prefer the visitor's nationality only inside that country.
 */
export const VISITOR_REVIEWS_REGION_TYPES = ["establishment", "sublocality"];

export const usesVisitorReviews = (regionType?: string | null): boolean =>
  VISITOR_REVIEWS_REGION_TYPES.includes(regionType ?? "");

/** Maps CloudFront / IP-derived ISO country to the reviews API `nationality` filter. */
export const COUNTRY_TO_TRUSTPILOT_NATIONALITY: Record<string, string> = {
  IN: "Indian",
  UK: "British",
  GB: "British",
  EN: "British",
  SG: "Singaporean",
  NP: "Nepalese",
  IE: "Irish",
  BD: "Bangladeshi",
  DE: "German",
  PK: "Pakistani",
  ES: "Spanish",
  AU: "Australian",
  CA: "Canadian",
  IT: "Italian",
  FR: "French",
  AE: "Emirati",
};

/**
 * Homepage (and establishment/sublocality search): reviews for the visitor's nationality when
 * known, else the global mixed set. China always uses Chinese students' reviews.
 */
export const nationalityForHomepage = (country?: string | null): string => {
  if (config.IS_CHINA_APP) return "China";
  const code = (country || "").trim().toUpperCase();
  return COUNTRY_TO_TRUSTPILOT_NATIONALITY[code] || "mixreviews";
};

/** Source-IP nationality for a search page, or null when the country is not in the map. */
export const sourceNationalityFromCountry = (country?: string | null): string | null => {
  if (!country) return null;
  return COUNTRY_TO_TRUSTPILOT_NATIONALITY[country.trim().toUpperCase()] ?? null;
};

/**
 * Keeps only what a review card shows: the text and its translations, the name, the university
 * and the nationality. The reviews API also returns each reviewer's email, booking and property;
 * anything left on a server-rendered review is published in the page's HTML.
 */
export const toCardReview = (review: Record<string, any>): Record<string, any> =>
  Object.fromEntries(
    Object.entries(review || {}).filter(
      ([key]) =>
        key === "edited_review" ||
        key.startsWith("edited_review_") ||
        ["user_name", "university", "nationality", "trustpilot_rating"].includes(key),
    ),
  );

/**
 * The homepage's bar for a review card (HomeV3 pickReviews): five stars, a named student,
 * and something to say. Chinese says as much in far fewer characters.
 */
export const isCardWorthyReview = (review: Record<string, any>): boolean => {
  const text = (config.IS_CHINA_APP && review?.edited_review_zh) || review?.edited_review || "";
  const name = (review?.user_name || "").trim().toLowerCase();
  return (
    (review?.trustpilot_rating ?? 5) >= 5 &&
    !!name &&
    name !== "student" &&
    text.length >= (config.IS_CHINA_APP ? 12 : 60)
  );
};

/** How long the server waits for the reviews before rendering without them, as on the homepage. */
export const REVIEWS_SSR_BUDGET_MS = 1500;

/** Resolves with the promise's value, or with null once `ms` has passed (calling onTimeout). */
export const withinBudget = <T>(
  promise: Promise<T>,
  ms: number,
  onTimeout?: () => void,
): Promise<T | null> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => {
      onTimeout?.();
      resolve(null);
    }, ms);
    promise
      .then((value) => resolve(value))
      .catch(() => resolve(null))
      .finally(() => clearTimeout(timer));
  });

const getUserCountry = async () => {
  try {
    const existingCookie = getCookie("userCountry");
    if (existingCookie) {
      return { country: existingCookie };
    }

    if (userCountryPromise) {
      return userCountryPromise;
    }

    userCountryPromise = (async () => {
      const rawOrigin =
        typeof window !== "undefined" ? window.location.origin : config.WEB_SERVICE_URL;
      const origin = rawOrigin?.endsWith("/") ? rawOrigin.slice(0, -1) : rawOrigin;
      const res = await axios.get(`${origin || ""}/places/getCountryData`, {
        timeout: 5000,
        headers: {
          Accept: "application/json",
        },
      });
      const { data } = res;
      // Set the cookie for one day (1 day)
      setCookie({ cname: "userCountry", cvalue: data.country, exdays: 1 });
      // Store city in localStorage if available
      if (typeof window !== "undefined" && data.city) {
        localStorage.setItem("city", data.city);
      }
      return data;
    })();

    const result = await userCountryPromise;
    return result;
  } catch (error) {
    userCountryPromise = null;
    if (
      typeof window !== "undefined" &&
      typeof window.Sentry !== "undefined" &&
      window.Sentry.captureException
    ) {
      window.Sentry.captureException("Error fetching user country:", error);
    }
    return { country: "GB" };
  }
};

export type TrustpilotReview = {
  user_name?: string;
  nationality?: string;
  university?: string;
  destination_city?: string;
  destination_country?: string;
  trustpilot_rating?: number | string;
  trustpilot_review?: string;
  edited_review?: string;
  edited_review_zh?: string;
  edited_review_es?: string;
  edited_review_fr?: string;
  edited_review_de?: string;
};

export const getTrustpilotData = async (
  destination_city: any,
  nationality: any,
  university = null,
  cancelToken?: any,
  destination_country?: string | null,
) => {
  try {
    const queryParams: Record<string, any> = {
      destination_city,
      nationality,
      university,
      limit: 20,
    };
    if (destination_country) queryParams.destination_country = destination_country;
    const { data } = await axios.get(`${config.API_URL}/api/v0/inventories/trustpilot_data`, {
      params: queryParams,
      cancelToken,
    });
    return data?.data;
  } catch (error: any) {
    return { error };
  }
};

export default getUserCountry;
